import { useState, useRef, useEffect, useCallback } from 'react';
import { useAuth } from '../context/AuthContext';
import { Card, CardContent } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { ScrollArea } from '../components/ui/scroll-area';
import axios from 'axios';
import { 
  Send, 
  Loader2,
  Sparkles,
  User,
  MapPin
} from 'lucide-react';
import { cn } from '../lib/utils';

const CaddieHat = ({ className }) => (
  <svg className={className} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <path d="M4 16s0-4 8-4 8 4 8 4" />
    <path d="M2 16h20" />
    <ellipse cx="12" cy="12" rx="6" ry="4" />
    <path d="M6 12c0-3.3 2.7-6 6-6s6 2.7 6 6" />
  </svg>
);

const API_URL = process.env.REACT_APP_BACKEND_URL + '/api';
const LOCATION_KEYWORDS = ['near', 'nearby', 'close', 'around', 'find', 'where', 'local', 'location', 'closest', 'my area'];

const CaddiePage = () => {
  const { user } = useAuth();
  const [messages, setMessages] = useState([
    {
      role: 'assistant',
      content: "Hi! I'm Caddie, your AI golf assistant powered by Gemini. I can find courses near you, recommend places to play anywhere in the country, and help book tee times. What would you like to know?"
    }
  ]);
  const [input, setInput] = useState('');
  const [loading, setLoading] = useState(false);
  const [userLocation, setUserLocation] = useState(null);
  const [locationStatus, setLocationStatus] = useState('idle');
  const messagesEndRef = useRef(null);

  useEffect(() => {
    scrollToBottom();
  }, [messages]);

  const scrollToBottom = () => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  };

  const requestLocation = useCallback(() => {
    return new Promise((resolve) => {
      if (!navigator.geolocation) {
        setLocationStatus('denied');
        resolve(null);
        return;
      }
      setLocationStatus('requesting');
      navigator.geolocation.getCurrentPosition(
        (position) => {
          const loc = { latitude: position.coords.latitude, longitude: position.coords.longitude };
          setUserLocation(loc);
          setLocationStatus('granted');
          resolve(loc);
        },
        () => { setLocationStatus('denied'); resolve(null); },
        { enableHighAccuracy: true, timeout: 10000, maximumAge: 300000 }
      );
    });
  }, []);

  const needsLocation = (text) => {
    const lower = text.toLowerCase();
    return LOCATION_KEYWORDS.some(kw => lower.includes(kw));
  };

  const handleShareLocation = async () => {
    const loc = await requestLocation();
    if (loc) {
      setMessages(prev => [...prev, { role: 'assistant', content: `Got your location! I can now find courses near you. What are you looking for?` }]);
    } else {
      setMessages(prev => [...prev, { role: 'assistant', content: "I couldn't access your location. You can still ask me about courses by mentioning a city or state!" }]);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!input.trim() || loading) return;

    const userMessage = input.trim();
    setInput('');
    setMessages(prev => [...prev, { role: 'user', content: userMessage }]);
    setLoading(true);

    let loc = userLocation;
    if (needsLocation(userMessage) && !loc) {
      loc = await requestLocation();
    }

    try {
      const payload = { query: userMessage };
      if (loc) {
        payload.latitude = loc.latitude;
        payload.longitude = loc.longitude;
      }
      const response = await axios.post(`${API_URL}/caddie/query`, payload);
      setMessages(prev => [...prev, { role: 'assistant', content: response.data.response }]);
    } catch {
      setMessages(prev => [...prev, { 
        role: 'assistant', 
        content: "I'm sorry, I couldn't process that request. Please try again or rephrase your question."
      }]);
    } finally {
      setLoading(false);
    }
  };

  const suggestedQueries = [
    "Find courses near me",
    "What events are happening this month?",
    "Help me book a tee time this weekend",
    "Tell me about golf leagues in Houston"
  ];

  return (
    <div className="h-[calc(100vh-8rem)] flex flex-col" data-testid="caddie-page">
      {/* Header */}
      <div className="flex items-center justify-between mb-6">
        <div className="flex items-center gap-4">
          <div className="w-12 h-12 rounded-full bg-gold flex items-center justify-center">
            <CaddieHat className="w-6 h-6 text-deep-green" />
          </div>
          <div>
            <h1 className="font-display font-bold text-2xl text-white">Caddie</h1>
            <p className="text-cream/70 text-sm">Your personal golf assistant powered by Gemini</p>
          </div>
        </div>
        <div className="flex items-center gap-2">
          {locationStatus === 'granted' ? (
            <span className="flex items-center gap-1 text-green-400 text-xs bg-green-400/10 px-2 py-1 rounded-full">
              <MapPin className="w-3 h-3" /> Location Active
            </span>
          ) : (
            <Button
              variant="outline"
              size="sm"
              onClick={handleShareLocation}
              className="text-cream/70 border-white/20 hover:bg-white/10 hover:text-white text-xs"
              data-testid="caddie-location-btn"
            >
              <MapPin className="w-3.5 h-3.5 mr-1.5" />
              Share Location
            </Button>
          )}
        </div>
      </div>

      {/* Chat Area */}
      <Card className="flex-1 flex flex-col bg-white/5 border-white/10 min-h-0">
        <CardContent className="flex-1 p-0 overflow-hidden">
          <ScrollArea className="h-full p-4">
            <div className="space-y-4">
              {messages.map((message, i) => (
                <div
                  key={i}
                  className={cn(
                    "flex gap-3",
                    message.role === 'user' && "flex-row-reverse"
                  )}
                >
                  <div className={cn(
                    "w-8 h-8 rounded-full flex items-center justify-center shrink-0",
                    message.role === 'assistant' ? "bg-gold" : "bg-fairway"
                  )}>
                    {message.role === 'assistant' ? (
                      <CaddieHat className="w-4 h-4 text-deep-green" />
                    ) : (
                      <User className="w-4 h-4 text-white" />
                    )}
                  </div>
                  <div
                    className={cn(
                      "max-w-[80%] rounded-sm px-4 py-3",
                      message.role === 'assistant'
                        ? "bg-white/10 text-white"
                        : "bg-gold text-deep-green"
                    )}
                  >
                    <p className="whitespace-pre-wrap">{message.content}</p>
                  </div>
                </div>
              ))}
              {loading && (
                <div className="flex gap-3">
                  <div className="w-8 h-8 rounded-full bg-gold flex items-center justify-center">
                    <CaddieHat className="w-4 h-4 text-deep-green" />
                  </div>
                  <div className="bg-white/10 rounded-sm px-4 py-3">
                    <div className="flex items-center gap-2 text-cream/70">
                      <Loader2 className="w-4 h-4 animate-spin" />
                      Thinking...
                    </div>
                  </div>
                </div>
              )}
              <div ref={messagesEndRef} />
            </div>
          </ScrollArea>
        </CardContent>

        {/* Suggested Queries */}
        {messages.length === 1 && (
          <div className="p-4 border-t border-white/10">
            <p className="text-cream/60 text-sm mb-3">Try asking:</p>
            <div className="flex flex-wrap gap-2">
              {suggestedQueries.map((query, i) => (
                <button
                  key={i}
                  onClick={() => setInput(query)}
                  className="px-3 py-1.5 bg-white/10 hover:bg-white/20 rounded-sm text-sm text-cream/90 transition-colors"
                  data-testid={`caddie-suggestion-${i}`}
                >
                  <Sparkles className="w-3 h-3 inline mr-1" />
                  {query}
                </button>
              ))}
            </div>
          </div>
        )}

        {/* Input */}
        <div className="p-4 border-t border-white/10">
          <form onSubmit={handleSubmit} className="flex gap-2">
            <Input
              value={input}
              onChange={(e) => setInput(e.target.value)}
              placeholder="Ask Caddie anything about golf..."
              className="flex-1 bg-white/10 border-white/20 text-white placeholder:text-white/50"
              disabled={loading}
              data-testid="caddie-input"
            />
            <Button 
              type="submit" 
              className="btn-gold"
              disabled={loading || !input.trim()}
              data-testid="caddie-send"
            >
              {loading ? (
                <Loader2 className="w-4 h-4 animate-spin" />
              ) : (
                <Send className="w-4 h-4" />
              )}
            </Button>
          </form>
        </div>
      </Card>
    </div>
  );
};

export default CaddiePage;
